import React from "react";
import { TextField, TextFieldProps, useTheme } from "@mui/material";
import { colors } from "../../config";

interface NumberInputProps extends Omit<TextFieldProps, "type" | "onChange" | "value"> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
}

export default function NumberInput({
  value,
  onChange,
  min = 1,
  ...textFieldProps
}: NumberInputProps) {
  const theme = useTheme();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "." || e.key === ",") {
      e.preventDefault();
    }
    textFieldProps.onKeyDown?.(e);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = Number(event.target.value) || 0;
    onChange(Math.max(numValue, min));
  };

  return (
    <TextField
      type="number"
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      sx={{
        width: 62,
        "& .MuiOutlinedInput-root": {
          height: 23,
          padding: 0,
          borderRadius: "6px",
          backgroundColor: colors.background.white,
          fontSize: "13px",
          textAlign: "center",
          lineHeight: theme.typography.body1.lineHeight,
          fontWeight: theme.typography.body1.fontWeight,
          color: theme.palette.text.primary,
          border: `1px solid ${colors.border.light}`,
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            borderColor: colors.primary.main,
          },
          "&.Mui-focused": {
            borderColor: colors.primary.main,
            boxShadow: `0 0 0 2px ${colors.primary.light}`,
          },
          "&.Mui-active": {
            borderColor: colors.primary.main,
          },
          "&.Mui-error": {
            borderColor: colors.error.input,
          },
          "&.Mui-disabled": {
            borderColor: colors.border.light,
            backgroundColor: colors.background.gray,
          },
          "& fieldset": {
            border: "none",
          },
        },
        "& .MuiOutlinedInput-input": {
          textAlign: "center",
          padding: 0,
          fontSize: "13px",
          "&::-webkit-outer-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },
          "&::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },
          MozAppearance: "textfield",
        },
      }}
      slotProps={{
        input: {
          inputProps: { min, onFocus: (e) => e.target.select() },
        },
      }}
      {...textFieldProps}
    />
  );
}

